import { Divider, Group, Select } from '@byecode/ui'
import {
    emptyTextField,
    type FlowNode,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    nodeTypeOptions,
    type NodeTypes,
    OtherPlatformNodeType,
    OUTPUT_SINGLE_DATA_NODES,
    useAtomData,
    type WechatGetOrderShippingActionConfig
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { WechatPaymentAccountSelect } from '@/components/WechatPaymentAccountSelect'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import OutputVariable from '../../../OutputVariable'
import { UnitNoAuth } from '../../Common/UnitNoAuth'
import * as CM from '../../styles'
import { ORDER_NUMBER_TYPE_OPTIONS, ORDER_NUMBER_TYPE_OUT_TRADE_NO, ORDER_NUMBER_TYPE_TRANSACTION_ID } from '../wechatShipping/constants'

interface WechatGetOrderShippingActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    actionTypeSwitcher?: React.ReactNode
    value: WechatGetOrderShippingActionConfig
    onChange: (value: WechatGetOrderShippingActionConfig) => void
}

export const WechatGetOrderShippingActionConfigure: React.FC<WechatGetOrderShippingActionConfigureProps> = ({
    nodeType,
    allParentNodes,
    actionTypeSwitcher,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const otherPlatformList = useAtomData(otherPlatformListAtom)
    const userOption = useGetUserRecordOptions()
    const miniProgramAccount = useMemo(() => otherPlatformList?.find(item => item.type === 'WECHAT_MINI_PROGRAM'), [otherPlatformList])

    const textUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType)),
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, workflowList]
    )

    const isOutTradeNo = (value.orderNumberType ?? ORDER_NUMBER_TYPE_TRANSACTION_ID) === ORDER_NUMBER_TYPE_OUT_TRADE_NO

    const header = (
        <Group label="动作配置">
            <ListItem4ByecodeUi>
                <CM.FormItemLabelWrapper>
                    <CM.FormItemLabel>类型</CM.FormItemLabel>
                </CM.FormItemLabelWrapper>
                <CM.FormItemContent>
                    {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                </CM.FormItemContent>
            </ListItem4ByecodeUi>
        </Group>
    )

    if (!miniProgramAccount) {
        return (
            <>
                {header}
                <UnitNoAuth type="WECHAT_MINI_PROGRAM" />
            </>
        )
    }

    return (
        <CM.Container>
            {header}
            <WechatPaymentAccountSelect
                value={value.paymentAccountId}
                onChange={paymentAccountId => onChange({ ...value, paymentAccountId })}
            />
            <Divider />
            <Group label="查询条件">
                <CM.FieldList>
                    <CM.FieldItem>
                        <CM.FieldTitle required>查询方式</CM.FieldTitle>
                        <Select
                            options={ORDER_NUMBER_TYPE_OPTIONS}
                            value={String(value.orderNumberType ?? ORDER_NUMBER_TYPE_TRANSACTION_ID)}
                            onChange={orderNumberType => onChange({ ...value, orderNumberType: Number(orderNumberType) })}
                        />
                    </CM.FieldItem>
                    {isOutTradeNo ? (
                        <CM.FieldItem>
                            <CM.FieldTitle required>商户订单号</CM.FieldTitle>
                            <NodeFieldPreview
                                innerTypes={getInputInnerTypeMatch('TEXT')}
                                disabled={disabledWithVersion}
                                field={emptyTextField}
                                value={value.merchantTradeNo}
                                onBlur={merchantTradeNo => onChange({ ...value, merchantTradeNo })}
                                autoHeight
                                placeholder="请输入商户订单号"
                                userOption={userOption}
                                upstreamOptions={textUpstreamOptions}
                                dataSourceList={dataSourceList}
                            />
                        </CM.FieldItem>
                    ) : (
                        <CM.FieldItem>
                            <CM.FieldTitle required>微信支付单号</CM.FieldTitle>
                            <NodeFieldPreview
                                innerTypes={getInputInnerTypeMatch('TEXT')}
                                disabled={disabledWithVersion}
                                field={emptyTextField}
                                value={value.transactionId}
                                onBlur={transactionId => onChange({ ...value, transactionId })}
                                autoHeight
                                placeholder="请输入微信支付单号"
                                userOption={userOption}
                                upstreamOptions={textUpstreamOptions}
                                dataSourceList={dataSourceList}
                            />
                        </CM.FieldItem>
                    )}
                </CM.FieldList>
            </Group>
            <Divider />
            <OutputVariable nodeType={OtherPlatformNodeType.WECHAT_GET_ORDER_SHIPPING} />
        </CM.Container>
    )
}
